import AppImage from '@/components/ui/AppImage';

interface TeamMember {
  id: number;
  name: string;
  role: string;
  tenure: string;
  image: string;
  alt: string;
  quote: string;
}

const teamMembers: TeamMember[] = [
{
  id: 1,
  name: 'Sophie Martin',
  role: 'Chef d\'Équipe',
  tenure: '5 ans chez Rouissi Services',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c0007e95-1763295050768.png",
  alt: 'Professional woman with brown hair in blue uniform smiling confidently in office setting',
  quote: 'J\'ai commencé comme agent de nettoyage et je suis maintenant chef d\'équipe. Les opportunités de croissance sont réelles ici.'
},
{
  id: 2,
  name: 'Marc Dubois',
  role: 'Technicien Spécialisé',
  tenure: '3 ans chez Rouissi Services',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_107713437-1763299815941.png",
  alt: 'Middle-aged man with short dark hair in professional cleaning uniform standing in commercial space',
  quote: 'La formation continue et le soutien de l\'équipe m\'ont permis de développer mes compétences techniques.'
},
{
  id: 3,
  name: 'Elena Rodriguez',
  role: 'Agent de Nettoyage',
  tenure: '2 ans chez Rouissi Services',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19af16ab3-1763294668278.png",
  alt: 'Young woman with long dark hair in cleaning uniform smiling warmly in residential setting',
  quote: 'L\'équilibre travail-vie personnelle et les horaires flexibles sont parfaits pour ma situation familiale.'
},
{
  id: 4,
  name: 'Thomas Weber',
  role: 'Superviseur',
  tenure: '4 ans chez Rouissi Services',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17280f6a4-1763301282153.png",
  alt: 'Professional man with glasses and short blonde hair in business casual attire in modern office',
  quote: 'La culture d\'entreprise valorise vraiment chaque membre de l\'équipe. C\'est un excellent environnement de travail.'
}];


interface TeamCultureSectionProps {
  className?: string;
}

const TeamCultureSection = ({ className = '' }: TeamCultureSectionProps) => {
  return (
    <section className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Notre Équipe & Culture
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Découvrez les visages derrière Rouissi Services et ce que nos employés disent de leur expérience professionnelle
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {teamMembers.map((member) =>
          <div
            key={member.id}
            className="bg-card rounded-lg shadow-sm border border-border overflow-hidden hover:shadow-md transition-shadow duration-300">

              <div className="relative h-64 overflow-hidden">
                <AppImage
                src={member.image}
                alt={member.alt}
                fill
                className="object-cover" />

              </div>
              <div className="p-6">
                <h3 className="text-lg font-heading font-bold text-foreground mb-1">
                  {member.name}
                </h3>
                <p className="text-sm text-primary font-semibold mb-1">
                  {member.role}
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  {member.tenure}
                </p>
                <p className="text-sm text-foreground italic leading-relaxed">
                  &quot;{member.quote}&quot;
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-primary/5 rounded-lg p-8 lg:p-12 border border-primary/20">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-4">
                Pourquoi Rejoindre Rouissi Services?
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-success-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-foreground">Environnement Multiculturel</p>
                    <p className="text-sm text-muted-foreground">Équipe diverse et inclusive avec respect mutuel</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-success-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-foreground">Formation & Développement</p>
                    <p className="text-sm text-muted-foreground">Programmes de formation continue et certifications professionnelles</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-success-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-foreground">Équilibre Vie-Travail</p>
                    <p className="text-sm text-muted-foreground">Horaires flexibles adaptés à vos besoins personnels</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-success-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-foreground">Reconnaissance & Récompenses</p>
                    <p className="text-sm text-muted-foreground">Programme mensuel de reconnaissance des performances</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative h-80 lg:h-96 rounded-lg overflow-hidden">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1429c2c1c-1766835774651.png"
                alt="Diverse team of cleaning professionals in uniform standing together smiling in bright modern office space"
                fill
                className="object-cover" />

            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default TeamCultureSection;